import * as React from 'react';

import { X } from 'lucide-react';

import { cn } from '../base/utils';
import { Badge } from './Badge';
import { Input } from './FormControls';

export type PillValue = string | number | boolean;
export type PillFieldType = 'string' | 'number' | 'boolean';

export interface PillInputMessages {
  stringPlaceholder: string;
  numberPlaceholder: string;
  booleanPlaceholder: string;
  removeValue: (value: PillValue) => string;
}

const defaultMessages: PillInputMessages = {
  stringPlaceholder: 'Enter a value and press Enter',
  numberPlaceholder: 'Enter a number and press Enter',
  booleanPlaceholder: 'Enter true or false and press Enter',
  removeValue: (value) => `Remove ${String(value)}`,
};

export interface PillInputProps {
  value?: PillValue[];
  onChange: (values: PillValue[]) => void;
  placeholder?: string;
  fieldType?: PillFieldType;
  disabled?: boolean;
  className?: string;
  messages?: Partial<PillInputMessages>;
}

export const convertPillValue = (value: string, type: PillFieldType): PillValue => {
  if (type === 'number') {
    const number = Number(value);
    return Number.isNaN(number) ? value : number;
  }
  if (type === 'boolean') return ['true', 'yes', '是', '1'].includes(value.toLowerCase().trim());
  return value;
};

export const isValidPillValue = (value: string, type: PillFieldType): boolean => {
  if (!value.trim()) return false;
  if (type === 'number') return !Number.isNaN(Number(value));
  if (type === 'boolean') {
    return ['true', 'false', 'yes', 'no', '是', '否', '1', '0'].includes(value.toLowerCase().trim());
  }
  return true;
};

export const PillInput = React.forwardRef<HTMLInputElement, PillInputProps>(
  (
    {
      value = [],
      onChange,
      placeholder,
      fieldType = 'string',
      disabled = false,
      className,
      messages: messageOverrides,
    },
    forwardedRef,
  ) => {
    const messages = { ...defaultMessages, ...messageOverrides };
    const [inputValue, setInputValue] = React.useState('');
    const localRef = React.useRef<HTMLInputElement | null>(null);
    const setInputRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        localRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef],
    );

    const removePill = React.useCallback(
      (index: number) => {
        if (!disabled) onChange(value.filter((_, currentIndex) => currentIndex !== index));
      },
      [disabled, onChange, value],
    );

    const addPill = React.useCallback(
      (candidate: string) => {
        const trimmed = candidate.trim();
        if (!isValidPillValue(trimmed, fieldType)) return;
        const converted = convertPillValue(trimmed, fieldType);
        if (value.some((current) => current === converted)) return;
        onChange([...value, converted]);
        setInputValue('');
      },
      [fieldType, onChange, value],
    );

    const resolvedPlaceholder = placeholder ?? (
      fieldType === 'number'
        ? messages.numberPlaceholder
        : fieldType === 'boolean'
          ? messages.booleanPlaceholder
          : messages.stringPlaceholder
    );

    return (
      <div
        className={cn(
          'flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm',
          disabled && 'cursor-not-allowed opacity-50',
          className,
        )}
        onClick={() => { if (!disabled) localRef.current?.focus(); }}
      >
        {value
          .filter((pill) => pill !== undefined && pill !== null && pill !== '')
          .map((pill, index) => (
            <Badge key={`${typeof pill}:${String(pill)}:${index}`} variant="secondary" className="flex items-center gap-1 py-1 text-xs">
              <span>{String(pill)}</span>
              {!disabled ? (
                <button
                  type="button"
                  className="rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={messages.removeValue(pill)}
                  onClick={(event) => { event.stopPropagation(); removePill(index); }}
                >
                  <X aria-hidden="true" className="size-3" />
                </button>
              ) : null}
            </Badge>
          ))}
        <Input
          ref={setInputRef}
          value={inputValue}
          onChange={(event) => setInputValue(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') { event.preventDefault(); addPill(inputValue); }
            else if (event.key === 'Backspace' && !inputValue && value.length > 0) removePill(value.length - 1);
            else if (event.key === 'Escape') localRef.current?.blur();
          }}
          placeholder={value.length === 0 ? resolvedPlaceholder : ''}
          disabled={disabled}
          className="h-auto min-w-20 flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>
    );
  },
);
PillInput.displayName = 'PillInput';
